import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../i18n/useTranslation';
import { Badge } from '../atoms/Badge/Badge';
import type { BadgeTone } from '../atoms/Badge/Badge.types';
import { Button } from '../atoms/Button/Button';
import { Card } from '../atoms/Card/Card';
import { Inline } from '../atoms/Inline/Inline';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { Stack } from '../atoms/Stack/Stack';
import { Text } from '../atoms/Typography/Text';
import { LogTestsButton } from '../measurements/LogTestsButton';
import { Section } from '../templates/Section/Section';
import { LoadErrorState } from '../feedback/LoadErrorState/LoadErrorState';
import { REEF_GUARD_FINDINGS_KEY, listReefGuardFindings, type ReefGuardFinding } from '../../services/reefGuardService';
import { useVoiceJournal } from '../capture/CaptureContext';
import './TodayCard.css';

export interface TodayCardProps {
  aquariumId: string;
}

const SEVERITY_TONE: Record<ReefGuardFinding['severity'], BadgeTone> = { critical: 'danger', warning: 'warning', hint: 'info' };
const SEVERITY_ORDER: Record<ReefGuardFinding['severity'], number> = { critical: 0, warning: 1, hint: 2 };

/**
 * "Heute" on the dashboard (usability audit §5.1): what needs doing now, above the
 * score and the parameter tiles. Open Reef Guard findings of this aquarium, with
 * overdue tests folded into one line and one "Log tests" action. Acknowledged
 * findings stay on the Alerts page only.
 */
export function TodayCard({ aquariumId }: TodayCardProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const voiceJournal = useVoiceJournal();
  const query = useQuery({ queryKey: REEF_GUARD_FINDINGS_KEY, queryFn: listReefGuardFindings, staleTime: 5 * 60_000 });

  const open = (query.data ?? [])
    .filter((f) => f.aquariumId === aquariumId && !f.acknowledged)
    .sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);
  const staleTests = open.filter((f) => f.ruleId === 'stale_test');
  const changes = open.filter((f) => f.ruleId !== 'stale_test');
  const parameterName = (f: ReefGuardFinding) => String(t(`measurement.history.parameterNames.${f.parameterId}`));

  return (
    <Section title={t('dashboard.today.title')}>
      <Card>
        {query.isPending ? (
          <LiveRegion visuallyHidden busy>{t('dashboard.today.loading')}</LiveRegion>
        ) : query.isError ? (
          <LoadErrorState
            compact
            titleLevel={3}
            live="polite"
            title={t('dashboard.today.unavailable')}
            onRetry={() => void query.refetch()}
            retryLabel={t('common.retry')}
          />
        ) : open.length === 0 ? (
          <Text color="muted">{t('dashboard.today.allClear')}</Text>
        ) : (
          <Stack gap="4">
            {changes.length > 0 && (
              <ul className="today-card__list">
                {changes.map((finding) => (
                  <li key={finding.id} className="today-card__item">
                    <Badge tone={SEVERITY_TONE[finding.severity]}>{t(`reefGuard.severity.${finding.severity}`)}</Badge>
                    <Text as="span">
                      {String(t(`reefGuard.rules.${finding.ruleId}.title`, { aquarium: finding.aquariumName, parameter: parameterName(finding) }))}
                    </Text>
                  </li>
                ))}
              </ul>
            )}
            {staleTests.length > 0 && (
              <Inline gap="3" align="center" justify="between" wrap>
                <Text>
                  {t('dashboard.today.overdueTests', {
                    parameters: new Intl.ListFormat(i18n.language, { type: 'unit' }).format(staleTests.map(parameterName)),
                  })}
                </Text>
                <Inline gap="2">
                  {voiceJournal === 'on' && (
                    <Button
                      variant="secondary"
                      onClick={() => navigate(`/aquariums/${encodeURIComponent(aquariumId)}/measurements/session/guided`)}
                    >
                      {t('dashboard.today.startGuided')}
                    </Button>
                  )}
                  <LogTestsButton aquariumId={aquariumId} id="dashboard-today-log-tests" variant="primary" />
                </Inline>
              </Inline>
            )}
            <Inline gap="2">
              <Button variant="ghost" size="sm" onClick={() => navigate('/alerts')}>
                {t('dashboard.today.viewAlerts')}
              </Button>
            </Inline>
          </Stack>
        )}
      </Card>
    </Section>
  );
}
